import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Download, Sparkles } from "lucide-react";
import { API } from "@/App";

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/lead-magnet`, { email, name, company });
      setDone(true);
      toast.success("Free chapter on its way!");
      // Trigger the actual PDF download
      const link = document.createElement("a");
      link.href = "/downloads/passion-centered-chapter-1.pdf";
      link.download = "The-Passion-Centered-Person-Chapter-1.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.error("Couldn't sign you up. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="free-chapter"
      data-testid="lead-magnet-section"
      className="relative bg-[#0a0a0a] py-24 md:py-28 border-t border-[#262626] overflow-hidden"
    >
      {/* Diagonal pink stripe background */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background:
            "linear-gradient(135deg, transparent 0%, transparent 40%, #EC1985 40%, #EC1985 60%, transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left: book mockup */}
        <div className="lg:col-span-5">
          <div className="relative max-w-sm mx-auto">
            <div
              className="aspect-[2/3] relative flex flex-col justify-between p-8 shadow-[20px_20px_0_0_#EC1985] border border-[#262626]"
              style={{
                background:
                  "linear-gradient(160deg, #1a1a1a 0%, #050505 100%)",
              }}
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#EC1985] font-bold mb-3">
                  Free Chapter
                </div>
                <BookOpen size={36} className="text-[#EC1985]" />
              </div>
              <div>
                <div className="font-display text-3xl text-white leading-tight">
                  The Passion<br />Centered Person
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">
                  By Gary Zelesky
                </div>
                <div className="mt-6 pt-4 border-t border-white/10 text-xs uppercase tracking-[0.25em] text-white/50 font-bold">
                  Chapter 1 · PDF
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <Sparkles size={18} className="text-[#EC1985]" />
            <span className="eyebrow">Free Download · No Catch</span>
          </div>
          <h2
            data-testid="lead-magnet-heading"
            className="h-display text-5xl md:text-6xl lg:text-7xl text-white mb-6"
          >
            Get Chapter 1<br />
            of <span className="text-[#EC1985]">The Passion<br />Centered Person.</span>
          </h2>
          <p className="text-white/75 text-lg leading-relaxed max-w-xl mb-8">
            The internal fuel that helps you rise, adjust, reconnect, and keep moving forward —
            delivered straight to your inbox. No spam. Unsubscribe anytime.
          </p>

          {!done ? (
            <form
              onSubmit={submit}
              data-testid="lead-magnet-form"
              className="surface p-6 md:p-8 space-y-4 max-w-xl"
            >
              {/* Honeypot — hidden from humans, filled by bots */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
              >
                <label>
                  Company (leave blank)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  data-testid="lead-magnet-name"
                  type="text"
                  placeholder="First name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-base"
                />
                <input
                  data-testid="lead-magnet-email"
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base"
                />
              </div>
              <button
                type="submit"
                data-testid="lead-magnet-submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? "Sending…" : (
                  <>
                    <Download size={18} /> Send Me the Free Chapter
                  </>
                )}
              </button>
              <p className="text-xs text-white/40 uppercase tracking-[0.15em] font-bold">
                Instant delivery · 100% free · One-click unsubscribe
              </p>
            </form>
          ) : (
            <div
              data-testid="lead-magnet-success"
              className="surface p-8 max-w-xl border-[#EC1985]"
            >
              <div className="flex items-center gap-3 text-[#EC1985] font-bold uppercase tracking-[0.2em] text-sm mb-4">
                <Sparkles size={18} /> You're In
              </div>
              <p className="text-white text-lg mb-4">
                Your download should start automatically. If it doesn't,
                <a
                  href="/downloads/passion-centered-chapter-1.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-magnet-fallback-link"
                  className="text-[#EC1985] underline ml-1 font-bold"
                >
                  click here to download Chapter 1
                </a>.
              </p>
              <p className="text-white/70 text-sm">
                Want Gary to bring this message to your team or event? Just{" "}
                <a href="#book" className="text-[#EC1985] underline font-bold">drop a note</a>{" "}
                and we'll reach out.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
